import type { ReactNode } from "react";
import type { Model } from "@lumen/shared";
import { t } from "../../i18n";
import type { ComposerActivity, ComposerVoice } from "./Composer";
import {
  TRIPO_ANIMATION_MODEL_ID,
  TRIPO_RIGGING_MODEL_ID,
  scenarioInputDefinitions,
} from "./ComposerParameterDefinitions";

export function ComposerQuickParameterBar({
  activity,
  model,
  supportedParameters,
  availableVoices,
  values,
  onChange,
  compact = false,
}: {
  activity: ComposerActivity;
  model?: Model;
  supportedParameters: string[];
  availableVoices: ComposerVoice[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  compact?: boolean;
}) {
  const advertised = model?.supportedParameters ?? supportedParameters;
  const supports = (keys: string[]) =>
    !advertised.length || keys.some((key) => advertised.includes(key));
  const scenario =
    model?.providerName?.toLowerCase().includes("scenario") ?? false;
  const tripo = model?.providerName?.toLowerCase().includes("tripo") ?? false;
  const tripoRigging = model?.remoteId === TRIPO_RIGGING_MODEL_ID;
  const tripoAnimation = model?.remoteId === TRIPO_ANIMATION_MODEL_ID;
  const tripoGeneration = tripo && !tripoRigging && !tripoAnimation;
  const tripoHSeries =
    model?.remoteId === "v3.0-20250812" || model?.remoteId === "v3.1-20260211";
  const tripoP1 = model?.remoteId === "P1-20260311";
  const supportsModelParameter = (keys: string[]) =>
    scenario ? false : tripo || supports(keys);
  const scenarioValues =
    values.scenarioParameters && typeof values.scenarioParameters === "object"
      ? (values.scenarioParameters as Record<string, unknown>)
      : {};
  const scenarioInputs = scenario
    ? scenarioInputDefinitions(model).filter(
        (input) =>
          input.name !== "prompt" &&
          input.type !== "file" &&
          input.type !== "file_array",
      )
    : [];
  const controls: ReactNode[] = [];
  const field = (
    key: string,
    label: string,
    control: ReactNode,
    className = "min-w-32",
  ) =>
    controls.push(
      <label key={key} className={`block ${className}`}>
        <span className="mb-1 block text-[10px] font-medium text-stone-500">
          {label}
        </span>
        {control}
      </label>,
    );
  const select = (
    key: string,
    label: string,
    options: Array<[string, string]>,
    className?: string,
  ) =>
    field(
      key,
      label,
      <select
        className="field py-1.5 text-xs"
        value={typeof values[key] === "string" ? String(values[key]) : ""}
        onChange={(event) => onChange(key, event.target.value || undefined)}
      >
        <option value="">{t("Default")}</option>
        {options.map(([value, optionLabel]) => (
          <option key={value} value={value}>
            {optionLabel}
          </option>
        ))}
      </select>,
      className,
    );
  const number = (
    key: string,
    label: string,
    min?: number,
    max?: number,
    step = 1,
    className?: string,
  ) =>
    field(
      key,
      label,
      <input
        type="number"
        className="field py-1.5 text-xs"
        min={min}
        max={max}
        step={step}
        value={typeof values[key] === "number" ? String(values[key]) : ""}
        placeholder={t("Default")}
        onChange={(event) =>
          onChange(
            key,
            event.target.value ? Number(event.target.value) : undefined,
          )
        }
      />,
      className,
    );
  const text = (key: string, label: string, className?: string) =>
    field(
      key,
      label,
      <input
        className="field py-1.5 text-xs"
        value={typeof values[key] === "string" ? String(values[key]) : ""}
        onChange={(event) => onChange(key, event.target.value || undefined)}
      />,
      className,
    );

  // Keep the composer focused on the controls people change most often. The
  // same component renders the complete model-specific set in Model options.
  if (compact) {
    if (
      (activity === "audio" || activity === "speech") &&
      availableVoices.length > 0 &&
      supportsModelParameter(["voice", "voices"])
    )
      field(
        "voice",
        t("Voice"),
        <select
          className="field py-1.5 text-xs"
          value={typeof values.voice === "string" ? values.voice : ""}
          onChange={(event) =>
            onChange("voice", event.target.value || undefined)
          }
        >
          <option value="">{t("Default")}</option>
          {availableVoices.map((voice) => (
            <option key={voice.id} value={voice.id}>
              {voice.label}
            </option>
          ))}
        </select>,
        "min-w-40",
      );
    if (activity === "image" && supportsModelParameter(["size", "image_size"]))
      select("size", t("Size"), [
        ["1024x1024", "1024 × 1024"],
        ["1792x1024", "1792 × 1024"],
        ["1024x1792", "1024 × 1792"],
      ]);
    if (
      activity === "video" &&
      supportsModelParameter(["aspect_ratio", "size"])
    )
      select("aspectRatio", t("Aspect ratio"), [
        ["16:9", "16:9"],
        ["9:16", "9:16"],
        ["1:1", "1:1"],
      ]);
    if (activity === "model3d") {
      if (supportsModelParameter(["engine", "target_engine"]))
        select("engine", t("Target engine"), [
          ["godot", "Godot"],
          ["web", "Web"],
          ["unity", "Unity"],
          ["unreal", "Unreal"],
        ]);
      if (supportsModelParameter(["output_format", "format"]))
        select("outputFormat", t("Export format"), [
          ["glb", "GLB"],
          ["gltf", "GLTF"],
          ["fbx", "FBX"],
          ["obj", "OBJ"],
          ["usdz", "USDZ"],
        ]);
      if (tripoRigging)
        select("rigType", t("Rig type"), [
          ["biped", "Biped"],
          ["quadruped", "Quadruped"],
          ["hexapod", "Hexapod"],
          ["octopod", "Octopod"],
          ["avian", "Avian"],
          ["serpentine", "Serpentine"],
          ["aquatic", "Aquatic"],
        ]);
      else if (tripoAnimation)
        select("animationPreset", t("Animation preset"), [
          ["preset:idle", "Idle"],
          ["preset:walk", "Walk"],
          ["preset:run", "Run"],
          ["preset:jump", "Jump"],
          ["preset:climb", "Climb"],
          ["preset:slash", "Slash"],
          ["preset:shoot", "Shoot"],
          ["preset:hurt", "Hurt"],
        ]);
    }
    if (
      (activity === "sprite" || activity === "tileset") &&
      supportsModelParameter(["columns"])
    )
      number("columns", t("Columns"), 1, 64);
    if (
      (activity === "sprite" || activity === "tileset") &&
      supportsModelParameter(["rows"])
    )
      number("rows", t("Rows"), 1, 64);
    if (!controls.length) return null;
    return (
      <div className="border-t border-stone-100 bg-stone-50/60 px-3 py-2 dark:border-white/10 dark:bg-white/[0.03]">
        <div className="flex flex-wrap items-end gap-2">{controls}</div>
      </div>
    );
  }

  if (
    (activity === "audio" || activity === "speech") &&
    availableVoices.length > 0 &&
    supportsModelParameter(["voice", "voices"])
  )
    field(
      "voice",
      t("Voice"),
      <select
        className="field py-1.5 text-xs"
        value={typeof values.voice === "string" ? values.voice : ""}
        onChange={(event) => onChange("voice", event.target.value || undefined)}
      >
        <option value="">{t("Default")}</option>
        {availableVoices.map((voice) => (
          <option key={voice.id} value={voice.id}>
            {voice.label}
          </option>
        ))}
      </select>,
      "min-w-40",
    );
  if (
    (activity === "audio" || activity === "speech") &&
    supportsModelParameter(["format", "response_format"])
  )
    select("format", t("Format"), [
      ["mp3", "MP3"],
      ["wav", "WAV"],
      ["opus", "Opus"],
    ]);
  if (activity === "image" && supportsModelParameter(["size", "image_size"])) {
    select("size", t("Size"), [
      ["1024x1024", "1024 × 1024"],
      ["1792x1024", "1792 × 1024"],
      ["1024x1792", "1024 × 1792"],
    ]);
  }
  if (activity === "image" && supportsModelParameter(["quality"])) {
    select("quality", t("Quality"), [
      ["standard", t("Standard")],
      ["hd", "HD"],
    ]);
  }
  if (
    activity === "video" &&
    supportsModelParameter(["aspect_ratio", "size"])
  ) {
    select("aspectRatio", t("Aspect ratio"), [
      ["16:9", "16:9"],
      ["9:16", "9:16"],
      ["1:1", "1:1"],
    ]);
  }
  if (activity === "video" && supportsModelParameter(["duration"])) {
    number("duration", t("Duration (seconds)"), 1, 120);
  }
  if (activity === "video" && supportsModelParameter(["fps", "frame_rate"])) {
    number("fps", "FPS", 1, 60);
  }
  if (activity === "model3d") {
    if (supportsModelParameter(["engine", "target_engine"]))
      select("engine", t("Target engine"), [
        ["godot", "Godot"],
        ["web", "Web"],
        ["unity", "Unity"],
        ["unreal", "Unreal"],
      ]);
    if (supportsModelParameter(["output_format", "format"]))
      select("outputFormat", t("Export format"), [
        ["glb", "GLB"],
        ["gltf", "GLTF"],
        ["fbx", "FBX"],
        ["obj", "OBJ"],
        ["usdz", "USDZ"],
      ]);
    if (tripoRigging) {
      select("rigType", t("Rig type"), [
        ["biped", "Biped"],
        ["quadruped", "Quadruped"],
        ["hexapod", "Hexapod"],
        ["octopod", "Octopod"],
        ["avian", "Avian"],
        ["serpentine", "Serpentine"],
        ["aquatic", "Aquatic"],
      ]);
      select("rigSpec", t("Bone naming"), [
        ["tripo", "Tripo"],
        ["mixamo", "Mixamo"],
      ]);
    } else if (tripoAnimation) {
      select("animationPreset", t("Animation preset"), [
        ["preset:idle", "Idle"],
        ["preset:walk", "Walk"],
        ["preset:run", "Run"],
        ["preset:jump", "Jump"],
        ["preset:climb", "Climb"],
        ["preset:slash", "Slash"],
        ["preset:shoot", "Shoot"],
        ["preset:hurt", "Hurt"],
      ]);
      field(
        "inputRigged",
        t("Input is already rigged"),
        <input
          type="checkbox"
          checked={values.inputRigged === true}
          onChange={(event) =>
            onChange("inputRigged", event.target.checked || undefined)
          }
        />,
        "flex min-h-9 items-end gap-1.5 pb-1 text-xs",
      );
      field(
        "bakeAnimation",
        t("Bake animation"),
        <input
          type="checkbox"
          checked={values.bakeAnimation !== false}
          onChange={(event) => onChange("bakeAnimation", event.target.checked)}
        />,
        "flex min-h-9 items-end gap-1.5 pb-1 text-xs",
      );
      field(
        "animateInPlace",
        t("Animate in place"),
        <input
          type="checkbox"
          checked={values.animateInPlace === true}
          onChange={(event) =>
            onChange("animateInPlace", event.target.checked || undefined)
          }
        />,
        "flex min-h-9 items-end gap-1.5 pb-1 text-xs",
      );
    } else if (
      !scenario &&
      (!tripo || !tripoP1) &&
      supportsModelParameter(["topology", "quad"])
    ) {
      select("topology", t("Topology"), [
        ["triangle", t("Triangles")],
        ["quad", t("Quads")],
      ]);
    }
    if (
      !scenario &&
      !tripoRigging &&
      !tripoAnimation &&
      supportsModelParameter([
        "targetPolycount",
        "target_polycount",
        "face_limit",
        "target_face_num",
      ])
    ) {
      number(
        "targetPolycount",
        t("Target polygons"),
        tripoP1 ? 48 : 100,
        tripoP1 ? 20000 : 300000,
        1,
        "min-w-36",
      );
    }
    if (!scenario && !tripo && supportsModelParameter(["texture_prompt"]))
      text("texturePrompt", t("Texture prompt"), "min-w-48");
    if (!scenario && !tripo && supportsModelParameter(["enable_pbr", "pbr"])) {
      field(
        "pbr",
        t("PBR materials"),
        <input
          type="checkbox"
          checked={values.pbr === true}
          onChange={(event) =>
            onChange("pbr", event.target.checked || undefined)
          }
        />,
        "flex min-h-9 items-end gap-1.5 pb-1 text-xs",
      );
    }
    if (tripoGeneration) {
      if (supportsModelParameter(["negative_prompt"]))
        text("negativePrompt", t("Negative prompt"), "min-w-48");
      if (supportsModelParameter(["texture_prompt"]))
        text("texturePrompt", t("Texture prompt"), "min-w-48");
      if (tripoHSeries && supportsModelParameter(["texture_quality"]))
        select("textureQuality", t("Texture quality"), [
          ["standard", t("Standard")],
          ["detailed", t("Detailed")],
          ["extreme", t("Extreme")],
        ]);
      if (supportsModelParameter(["model_seed"]))
        number(
          "modelSeed",
          t("Model seed"),
          undefined,
          undefined,
          1,
          "min-w-28",
        );
    }
  }
  if (
    (activity === "sprite" || activity === "tileset") &&
    supportsModelParameter(["columns"])
  ) {
    number("columns", t("Columns"), 1, 64);
  }
  if (
    (activity === "sprite" || activity === "tileset") &&
    supportsModelParameter(["rows"])
  ) {
    number("rows", t("Rows"), 1, 64);
  }
  if (
    (activity === "sprite" || activity === "tileset") &&
    supportsModelParameter(["tile_size"])
  ) {
    number("tileSize", t("Tile size"), 1, 4096);
  }
  if (scenarioInputs.length > 0)
    for (const input of scenarioInputs) {
      const name = String(input.name);
      const label = typeof input.label === "string" ? input.label : name;
      const type = typeof input.type === "string" ? input.type : "string";
      const current = scenarioValues[name];
      if (type === "boolean") {
        controls.push(
          <label
            key={`scenario-${name}`}
            className="flex min-h-9 items-end gap-1.5 pb-1 text-xs"
          >
            <input
              type="checkbox"
              checked={Boolean(current)}
              onChange={(event) =>
                onChange("scenarioParameters", {
                  ...scenarioValues,
                  [name]: event.target.checked,
                })
              }
            />
            {label}
          </label>,
        );
      } else if (
        Array.isArray(input.allowedValues) &&
        input.allowedValues.length > 0
      ) {
        field(
          `scenario-${name}`,
          label,
          <select
            className="field py-1.5 text-xs"
            value={current === undefined ? "" : String(current)}
            onChange={(event) =>
              onChange("scenarioParameters", {
                ...scenarioValues,
                [name]: event.target.value || undefined,
              })
            }
          >
            <option value="">{t("Default")}</option>
            {input.allowedValues.map((value) => (
              <option key={String(value)} value={String(value)}>
                {String(value)}
              </option>
            ))}
          </select>,
        );
      } else if (type === "number") {
        field(
          `scenario-${name}`,
          label,
          <input
            type="number"
            className="field py-1.5 text-xs"
            min={typeof input.min === "number" ? input.min : undefined}
            max={typeof input.max === "number" ? input.max : undefined}
            step={typeof input.step === "number" ? input.step : 1}
            value={typeof current === "number" ? String(current) : ""}
            onChange={(event) =>
              onChange("scenarioParameters", {
                ...scenarioValues,
                [name]: event.target.value
                  ? Number(event.target.value)
                  : undefined,
              })
            }
          />,
        );
      }
    }
  if (supports(["temperature"]))
    number("temperature", t("Temperature"), 0, 2, 0.1);
  if (supports(["top_p"])) number("topP", t("Top P"), 0, 1, 0.05);
  if (supports(["seed"])) number("seed", t("Seed"), -2147483648, 2147483647);
  if (!controls.length) return null;
  return (
    <div className="border-t border-stone-100 bg-stone-50/60 px-3 py-2 dark:border-white/10 dark:bg-white/[0.03]">
      <div className="mb-1 text-[10px] font-semibold tracking-wide text-stone-400 uppercase">
        {t("Model parameters")}
      </div>
      <div className="flex flex-wrap items-end gap-2">{controls}</div>
    </div>
  );
}
